import React, { useCallback, useMemo, useRef } from 'react';
import Chart from 'react-apexcharts';
import { useScreenshot } from 'use-react-screenshot';
import constants from 'utils/constants';
import { getVisibleHetrojobs, buildPerJobFromScenarioData, isValidCppData, mapCppRowToPerJob } from './heterojobChartUtils';

const colorMap = {
  CAPEX: '#3996FF',
  Recurring: '#B0D5FF',
  Material: '#A1A19D',
  Consumables: '#D5D5D3',
};

const MultipleScenariosChart = ({ onCapture, scenariosData, scenarios, costData, cppData, currency = '' }) => {
  const chartRef = useRef(null);
  const [, takeScreenshot] = useScreenshot();

  const sortedScenarios = useMemo(
    () =>
      [...scenarios].sort((a, b) => {
        if (a === constants.DEFAULT_SCENARIO) return -1;
        if (b === constants.DEFAULT_SCENARIO) return 1;
        return 0;
      }),
    [scenarios]
  );

  const scenarioValues = useMemo(() => {
    const values = [];
    sortedScenarios
      .filter((scenario) => scenariosData[scenario])
      .forEach((scenario, index) => {
        const data = scenariosData[scenario];
        const capex = parseFloat(data?.system_cost_per_part?.value || 0);
        const recurring = parseFloat(data?.recurring_cost_per_part?.value || 0);
        const material = parseFloat(data?.material_cost_per_part?.value || 0);
        const consumables = parseFloat(data?.cpp_consumables?.value || 0);
        const calculatedTotal = capex + recurring + material + consumables;
        let total = calculatedTotal;
        const parsed = parseFloat(data?.total_am_cpp?.value);
        if (Number.isFinite(parsed) && parsed > 0) total = parsed;
        if (!Number.isFinite(total) || total < 0) total = calculatedTotal;

        const visibleHetrojobs = getVisibleHetrojobs(data?.hetrojobs);

        if (visibleHetrojobs.length > 0) {
          const isDefaultScenario = scenario === constants.DEFAULT_SCENARIO;
          const useCppData = isDefaultScenario && costData && isValidCppData(cppData, visibleHetrojobs.length);
          let perJob = useCppData && Array.isArray(cppData)
            ? cppData.map((row) => mapCppRowToPerJob(row)).filter(Boolean)
            : buildPerJobFromScenarioData(data, visibleHetrojobs);

          if (perJob && perJob.length > 0) {
            perJob.forEach((job, jobIndex) => {
              values.push({
                scenarioName: scenario,
                partLabel: `${scenario} - Part ${jobIndex + 1}`,
                ...job,
              });
            });
          } else {
            visibleHetrojobs.forEach((_, jobIndex) => {
              values.push({
                scenarioName: scenario,
                partLabel: `${scenario} - Part ${jobIndex + 1}`,
                capex,
                recurring,
                material,
                consumables,
                total,
              });
            });
          }
        } else {
          values.push({
            scenarioName: scenario,
            partLabel: `${scenario} - Part 1`,
            capex,
            recurring,
            material,
            consumables,
            total,
          });
        }
      });
    return values;
  }, [sortedScenarios, scenariosData, costData, cppData]);

  const calculateYAxisMax = useCallback((total, minValue) => {
    if (!Number.isFinite(total) || total <= 0) {
      return 20;
    }
    
    const buffer = Math.max(total * 0.02, minValue || 0);
    const adjustedTotal = total + buffer;
    const magnitude = Math.pow(10, Math.floor(Math.log10(adjustedTotal)));
    const normalized = adjustedTotal / magnitude;
    
    let rounded;
    if (normalized <= 1.1) {
      rounded = 1.2;
    } else if (normalized <= 1.2) {
      rounded = 1.2;
    } else if (normalized <= 1.3) {
      rounded = 1.3;
    } else if (normalized <= 1.4) {
      rounded = 1.4;
    } else if (normalized <= 1.5) {
      rounded = 1.5;
    } else if (normalized <= 2) {
      rounded = 2;
    } else if (normalized <= 2.5) {
      rounded = 2.5;
    } else if (normalized <= 5) {
      rounded = 5;
    } else {
      rounded = 10;
    }
    
    let maxValue = rounded * magnitude;
    const maxAllowed = adjustedTotal * 1.1;
    if (maxValue > maxAllowed) {
      const smallerMagnitude = Math.pow(10, Math.floor(Math.log10(adjustedTotal * 1.1)));
      const normalizedMax = (adjustedTotal * 1.1) / smallerMagnitude;
      
      if (normalizedMax <= 1.2) {
        maxValue = 1.2 * smallerMagnitude;
      } else if (normalizedMax <= 1.5) {
        maxValue = 1.5 * smallerMagnitude;
      } else if (normalizedMax <= 2) {
        maxValue = 2 * smallerMagnitude;
      } else if (normalizedMax <= 2.5) {
        maxValue = 2.5 * smallerMagnitude;
      } else if (normalizedMax <= 5) {
        maxValue = 5 * smallerMagnitude;
      } else {
        maxValue = 10 * smallerMagnitude;
      }
    }
    
    return maxValue;
  }, []);

  const minNonZeroValue = useMemo(() => {
    const allValues = scenarioValues.flatMap(sv => [
      sv.capex,
      sv.recurring,
      sv.material,
      sv.consumables,
    ]).filter(v => v > 0);
    return allValues.length > 0 ? Math.min(...allValues) : 0;
  }, [scenarioValues]);

  const maxTotal = useMemo(() => {
    return Math.max(...scenarioValues.map(sv => sv.total), 0);
  }, [scenarioValues]);

  const calculateAdjustedValues = useCallback((values, total) => {
    const minVisualHeight = Math.max(total * 0.02, 0.1);
    let totalAdjustment = 0;
    const adjustedValues = values.map(item => {
      if (item.value > 0 && item.value < minVisualHeight) {
        const adjustment = minVisualHeight - item.value;
        totalAdjustment += adjustment;
        return { ...item, adjusted: minVisualHeight };
      }
      return { ...item, adjusted: item.value };
    });
    
    if (totalAdjustment > 0) {
      const largeSegments = adjustedValues.filter(v => v.value >= minVisualHeight);
      const largeSegmentsTotal = largeSegments.reduce((sum, v) => sum + v.value, 0);
      
      if (largeSegmentsTotal > 0) {
        largeSegments.forEach(largeSegment => {
          const ratio = largeSegment.value / largeSegmentsTotal;
          const adjustment = totalAdjustment * ratio;
          const item = adjustedValues.find(v => v.name === largeSegment.name);
          if (item) {
            item.adjusted = item.value - adjustment;
          }
        });
      }
    }
    
    return adjustedValues;
  }, []);

  // Determine category order based on aggregated values across all scenarios
  const categoryOrder = useMemo(() => {
    const aggregatedValues = scenarioValues.reduce((acc, sv) => {
      acc.capex = (acc.capex || 0) + sv.capex;
      acc.recurring = (acc.recurring || 0) + sv.recurring;
      acc.material = (acc.material || 0) + sv.material;
      acc.consumables = (acc.consumables || 0) + sv.consumables;
      return acc;
    }, { capex: 0, recurring: 0, material: 0, consumables: 0 });

    const values = [
      { name: 'CAPEX', value: aggregatedValues.capex || 0 },
      { name: 'Recurring', value: aggregatedValues.recurring || 0 },
      { name: 'Material', value: aggregatedValues.material || 0 },
      { name: 'Consumables', value: aggregatedValues.consumables || 0 },
    ];
    
    const total = values.reduce((sum, item) => sum + item.value, 0);
    const adjustedValues = calculateAdjustedValues(values, total);
    
    return [...adjustedValues]
      .filter(item => item.value > 0)
      .sort((a, b) => b.adjusted - a.adjusted)
      .map(item => item.name);
  }, [scenarioValues, calculateAdjustedValues]);

  const yAxisMax = useMemo(
    () => calculateYAxisMax(maxTotal, minNonZeroValue),
    [maxTotal, minNonZeroValue, calculateYAxisMax]
  );

  const capture = useCallback(async () => {
    const target = chartRef.current;
    if (!target) {
      return;
    }
    const img = await takeScreenshot(target);
    if (img && onCapture) {
      onCapture(img);
    }
  }, [takeScreenshot, onCapture]);

  const totalsByIndex = useMemo(
    () =>
      scenarioValues.map((sv) => {
        const t = Number(sv.total);
        return Number.isFinite(t) && t >= 0 ? t : 0;
      }),
    [scenarioValues]
  );

  const options = useMemo(
    () => ({
      chart: {
        id: 'cost-per-part-multiple',
        stacked: true,
        toolbar: { show: false },
        zoom: { enabled: false },
        events: {
          mounted: capture,
          updated: capture,
        },
      },
      colors: categoryOrder.map(cat => colorMap[cat] || '#3F8FF1'),
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '45%',
          endingShape: 'flat',
          dataLabels: {
            total: {
              enabled: true,
              formatter: (_val, opts) => {
                const idx = opts?.dataPointIndex ?? 0;
                const total = totalsByIndex[idx];
                return total != null && Number.isFinite(total)
                  ? `${total.toFixed(2)}${currency ? ` ${currency}` : ''}`
                  : '';
              },
              style: {
                fontSize: '12px',
                fontWeight: 600,
                colors: ['#1f1f1f'],
              },
            },
          },
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        show: true,
        width: 1,
        colors: ['#ffffff'],
      },
      xaxis: {
        categories: scenarioValues.map(sv => sv.partLabel),
        axisBorder: { show: true, color: '#CFCFCF' },
        labels: {
          style: {
            colors: 'black',
            fontSize: '14px',
            fontWeight: 700,
          },
        },
      },
      yaxis: {
        min: 0,
        max: yAxisMax,
        tickAmount: 5,
        labels: {
          formatter: (val) => (Number.isFinite(val) ? val.toFixed(0) : '0'),
          style: {
            colors: '#575757',
            fontSize: '14px',
          },
        },
        title: {
          text: 'Total AM Cost per Part',
          style: {
            fontSize: '14px',
            fontWeight: 700,
            color: 'black',
          },
        },
      },
      legend: {
        position: 'right',
        offsetY: 0,
        fontSize: '11px',
      },
      grid: {
        borderColor: '#E0E0E0',
        strokeDashArray: 3,
        yaxis: {
          lines: { show: true },
        },
      },
      tooltip: {
        y: {
          formatter: (val) => {
            return val.toFixed(2);
          },
        },
      },
    }),
    [capture, scenarioValues, yAxisMax, categoryOrder, currency, totalsByIndex],
  );

  const series = useMemo(
    () => {
      return categoryOrder.map(categoryName => {
        const data = scenarioValues.map(sv => {
          let value = 0;
          switch(categoryName) {
            case 'CAPEX':
              value = sv.capex || 0;
              break;
            case 'Recurring':
              value = sv.recurring || 0;
              break;
            case 'Material':
              value = sv.material || 0;
              break;
            case 'Consumables':
              value = sv.consumables || 0;
              break;
            default:
              value = 0;
              break;
          }
          
          // Apply adjustment per scenario for visibility
          const scenarioTotal = sv.total;
          const scenarioValuesArray = [
            { name: 'CAPEX', value: sv.capex || 0 },
            { name: 'Recurring', value: sv.recurring || 0 },
            { name: 'Material', value: sv.material || 0 },
            { name: 'Consumables', value: sv.consumables || 0 },
          ];
          const adjustedScenarioValues = calculateAdjustedValues(scenarioValuesArray, scenarioTotal);
          const adjustedValue = adjustedScenarioValues.find(item => item.name === categoryName);
          
          return adjustedValue ? adjustedValue.adjusted : value;
        });
        
        return {
          name: categoryName,
          data: data,
        };
      });
    },
    [categoryOrder, scenarioValues, calculateAdjustedValues],
  );

  return (
    <div
      ref={chartRef}
      aria-hidden
      style={{
        position: 'absolute',
        top: '-9999px',
        left: '-9999px',
        background: '#fff',
        pointerEvents: 'none',
        boxSizing: 'border-box',
      }}
    >
      <Chart options={options} series={series} type="bar" width={650} height={400} />
    </div>
  );
};

export default MultipleScenariosChart;
